import React, { createContext, useContext, useState, useEffect } from 'react';
import * as allauthApi from '../services/allauthApi';
import { logger } from '../services/utils';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const sessionData = await allauthApi.getSession();

      // Check if user is authenticated based on allauth response structure
      if (sessionData?.meta?.is_authenticated && sessionData?.data?.user) {
        setUser(sessionData.data.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      logger.error('AuthContext', 'Failed to check auth', err);
      setUser(null);
      setIsAuthenticated(false);
      setError('Failed to check authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await allauthApi.login(email, password);

      // Check allauth response structure
      if (response?.meta?.is_authenticated && response?.data?.user) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return true;
      } else {
        throw new Error('Invalid login response');
      }
    } catch (err) {
      logger.error('AuthContext', 'Login failed', err);
      setError(err.response?.data?.errors?.[0]?.message || 'Login failed. Please check your credentials.');
      setIsAuthenticated(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await allauthApi.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      logger.error('AuthContext', 'Logout failed', err);
      setError('Logout failed');
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
